/**
 * `/c/:shareToken` — the ring as a gift. Read-only: no editing, no sources, no
 * account. Pins a viewer makes are theirs alone and live in this browser.
 *
 * The token may be a calendar's or a circle's. The worker has already re-rooted
 * a circle's bundle (docs/CIRCLES.md §12), so the page draws it exactly as it
 * draws a whole ring; all that differs is the name over it and where a viewer's
 * own pins are kept, since the same person may hold both links.
 */
import type { CalEvent, EventPin, Source, Tag, View } from '@circle/shared';
import { ApiError, api } from '../api';
import { LocalPinStore } from '../../core/pins';
import type { PageContext, Teardown } from '../router';
import { Panel } from '../../ui/panel';
import { RingView, type RingBundle } from '../../ui/ring-view';
import { TopBar, footerBar, trackFooterHeight } from '../../ui/topbar';
import { button, el } from '../../ui/dom';
import { openDetail } from '../../ui/detail-panel';
import { openCirclesPanel } from '../../ui/circles-panel';
import { CirclesDock } from '../../ui/circles-dock';
import { SearchField, eventFilter } from '../../ui/search';
import { ViewsBar } from '../../ui/views-bar';
import { WorkingLayout, completeLayout, defaultView } from '../../ui/circles';
import { errorState, loadingState } from '../../ui/states';
import { errorMessage, toast, toastError } from '../../ui/toast';

export async function renderViewerPage(ctx: PageContext): Promise<Teardown> {
  const token = ctx.params.token!;
  ctx.host.appendChild(loadingState('Opening the ring'));

  let shared;
  try {
    shared = await api.getShared(token);
  } catch (err) {
    ctx.host.replaceChildren(notShared(err));
    return;
  }

  const calendarId = shared.calendar.id;
  const circle = shared.circle;
  const events: CalEvent[] = shared.events;
  // A viewer arranges their own copy: pins stay in this browser and are never
  // sent anywhere (SPEC, "Pins"). A circle's ring is a different ring from the
  // calendar's, so its pins are kept apart.
  const pins = new LocalPinStore(circle ? `${calendarId}:${circle.id}` : calendarId);
  // The shared projection strips a source down to what a viewer may see. The
  // panels take a `Source`, whose only other required field is the calendar it
  // belongs to — which this page knows — so one is made rather than asserted.
  const sources: Source[] = shared.sources.map((s) => ({ ...s, calendarId }));
  const tags: Tag[] = shared.tags ?? [];
  const views: View[] = shared.views ?? [];
  const eventPins: EventPin[] = shared.eventPins ?? [];

  ctx.host.replaceChildren();
  document.documentElement.dataset.surface = 'scene';

  const sceneHost = el('div', { class: 'scene-host' });
  ctx.host.appendChild(sceneHost);
  const today = new Date();

  // Only what the ring draws: the scene reads the theme, the sources, the
  // entries and the circles, and the pins the owner made. Nothing here has to
  // stand in for the owner's record of the calendar.
  const bundle = (): RingBundle => ({ calendar: shared.calendar, sources, events, tags, eventPins });

  // A viewer opens on the owner's default view and may switch between the
  // others. Everything they change from there is theirs alone: the working
  // layout has no `save`, so it never leaves this browser.
  let currentViewId: string | null = defaultView(views)?.id ?? null;
  const opening = views.find((v) => v.id === currentViewId);

  const ring = new RingView({
    host: sceneHost,
    bundle: bundle(),
    today,
    pins,
    layout: completeLayout(tags, opening?.layout),
    // The view's own camera, under the hash and over a fit of its layout.
    ...(opening?.camera ? { camera: opening.camera } : {}),
    // A fit aims at the part of the page the panel is not sitting on.
    insets: () => panel.insets(),
    onSelect: (selection) => {
      // A band or a ring line picks out its circle; a viewer sees it in the
      // panel as they would on the owner's page, without the editing half.
      if (selection?.kind === 'circle') {
        openCirclesPanel({
          panel,
          calendarId,
          tags: () => tags,
          events: () => events,
          sources: () => sources,
          working,
          editable: false,
          reload: () => Promise.resolve(),
          expand: selection.tagId,
        });
        return;
      }
      if (selection?.kind !== 'event') {
        panel.close();
        return;
      }
      const event = events.find((e) => e.id === selection.eventId);
      if (!event) return;
      openDetail({
        panel,
        calendarId,
        event,
        source: sources.find((s) => s.id === event.sourceId),
        viewYear: ring.year,
        today,
        editable: false,
        tags: () => tags,
        onHighlightRing: (ringId) => ring.highlightRing(ringId),
        onChanged: () => {},
        onDeleted: () => {},
        onClose: () => ring.select(null),
      });
    },
    onYearChange: (year) => {
      topbar.setYear(year);
      dock.setYear(year);
    },
  });

  const panel = new Panel(ctx.host);

  /** The same one matcher the owner's page uses, for the ring and the dock. */
  function applySearch(query: string): void {
    const keep = eventFilter(query, new Map(tags.map((t) => [t.id, t.name])));
    ring.setFilter(keep);
    dock.setSearch(keep);
  }

  const search = new SearchField({ onChange: applySearch });
  const detachSearch = search.attach();

  // No `save`: a viewer's arrangement is theirs alone and never leaves this
  // browser, so there is nothing for the working layout to be dirty against
  // and the views bar shows them no Save or Revert.
  const working = new WorkingLayout({
    apply: (layout, animate) => ring.setViewLayout(layout, { animate }),
  });
  // `apply: false`: the RingView opened on this layout, and re-applying it
  // would refit over the camera it chose.
  working.adopt(currentViewId, completeLayout(tags, opening?.layout), { apply: false });

  const viewsBar = new ViewsBar({
    calendarId,
    views: () => views,
    current: () => currentViewId,
    editable: false,
    onSelect: (view) => {
      currentViewId = view.id;
      working.adopt(view.id, completeLayout(tags, view.layout), { animate: true });
      if (view.camera) ring.setCamera(view.camera);
      viewsBar.paint();
    },
    capture: () => ({ layout: working.current, camera: ring.camera() }),
    reload: () => Promise.resolve(),
  });

  const topbar = new TopBar({
    name: circle?.name ?? shared.calendar.name,
    year: ring.year,
    editableName: false,
    onYear: (year) => ring.setYear(year),
    search,
    actions: [
      el('span', { class: 'badge', text: 'View only' }),
      circle ? el('span', { class: 'badge', text: `A circle from ${shared.calendar.name}` }) : null,
      tags.length
        ? button(
            'Circles',
            () =>
              openCirclesPanel({
                panel,
                calendarId,
                tags: () => tags,
                events: () => events,
                sources: () => sources,
                working,
                editable: false,
                reload: () => Promise.resolve(),
              }),
            { class: 'btn--small' },
          )
        : null,
    ],
  });

  const footer = footerBar(
    [
      views.length > 1 ? viewsBar.root : null,
      el(
        'div',
        { class: 'made-with' },
        el('span', { text: 'Made with Circles. ' }),
        el('a', { href: '/', text: 'Make your own' }),
      ),
    ],
    [
      button('Reset view', () => ring.resetView(), { class: 'btn--small btn--quiet' }),
      button(
        'Reset layout',
        () => {
          try {
            ring.resetPins();
            toast('Your arrangement is cleared. The owner’s layout is back.');
          } catch (err) {
            toastError(err);
          }
        },
        { class: 'btn--small btn--quiet' },
      ),
    ],
  );

  // A viewer gets the dock too: the eyes work on their own copy of the
  // arrangement, the same way the panel's do, a circle's name narrows the ring
  // to it, and "Coming up" reads the year forwards. There is no panel to open
  // from a row, which is the only half of it an owner has and a viewer does
  // not. A ring with no circles at all still gets one, for the list alone.
  const dock = new CirclesDock({
    calendarId,
    tags: () => tags,
    events: () => events,
    sources: () => sources,
    working,
    editable: false,
    reload: () => Promise.resolve(),
    year: ring.year,
    today,
    onOpen: () => {},
    onSelectEvent: (eventId) => {
      ring.select({ kind: 'event', eventId });
      ring.revealSelection(panel.insets());
    },
    onHighlight: (tagId) => ring.highlightRing(tagId),
  });

  ctx.host.append(topbar.root, dock.root, footer);
  // The bottom sheet stops where the strip starts; only a measurement knows
  // how tall the strip is (ui/topbar.ts).
  const untrackFooter = trackFooterHeight(footer);

  return () => {
    untrackFooter();
    detachSearch();
    search.destroy();
    dock.destroy();
    working.destroy();
    ring.destroy();
    panel.destroy();
    viewsBar.destroy();
    topbar.destroy();
    delete document.documentElement.dataset.surface;
  };
}

function notShared(err: unknown): HTMLElement {
  const home = el('a', { class: 'btn', href: '/', text: 'Make your own ring' });
  if (err instanceof ApiError && err.status === 404) {
    return errorState({
      title: 'This link no longer opens anything',
      description: 'The owner has stopped sharing it, or replaced it with a new one. Ask them for the current link.',
      actions: [home],
    });
  }
  return errorState({
    title: 'The ring did not load',
    description: errorMessage(err),
    actions: [home],
  });
}
